import type { Component } from 'vue';
import {
  PhHandWaving,
  PhPlusCircle,
  PhQueue,
  PhCalendarBlank,
  PhMonitorArrowUp,
  PhChartLineUp,
  PhScreencast,
  PhGear,
  PhQuestion,
} from '@phosphor-icons/vue';

export type NavSubItemConfig = {
  label: string;
  path?: string;
  icon?: Component;
  exact?: boolean;
  areaAccessName?: string;
  checkIsVisible?: (user: any) => boolean;
};

export type NavItemConfig = {
  label: string;
  path?: string;
  icon: Component;
  exact?: boolean;
  /** Read-access area name from the user store. */
  areaAccessName?: string;
  /** Custom predicate gating visibility. */
  checkIsVisible?: (user: any) => boolean;
  /** When present, item renders as an inline dropdown. */
  subItems?: NavSubItemConfig[];
};

/** Top list (Figma `nav-item-list-1`). */
export const topNavItems: NavItemConfig[] = [
  {
    label: 'Start',
    path: '/',
    icon: PhHandWaving,
    exact: true,
    areaAccessName: 'Common',
  },
  {
    label: 'Media',
    path: '/media',
    icon: PhPlusCircle,
    areaAccessName: 'MediaLibrary',
  },
  {
    label: 'Playlists',
    path: '/playlists',
    icon: PhQueue,
    areaAccessName: 'Playlists',
  },
  {
    label: 'Campaigns',
    path: '/campaigns',
    icon: PhCalendarBlank,
    areaAccessName: 'Campaigns',
  },
  {
    label: 'Players',
    path: '/players',
    icon: PhMonitorArrowUp,
    areaAccessName: 'Players',
  },
  {
    label: 'Analytics',
    icon: PhChartLineUp,
    subItems: [
      { label: 'Player Analytics', path: '/analytics' },
      {
        label: 'Proof of Play Reports',
        path: '/reports',
        checkIsVisible: (user) => user.isAnalytics(),
      },
    ],
    checkIsVisible: (user) => user.isAnalytics(),
  },
  {
    label: 'Live Streams',
    path: '/live-streams',
    icon: PhScreencast,
    checkIsVisible: (user) => user.hasAccessToFeature('liveStream'),
  },
];

/**
 * Bottom list (Figma `nav-item-list-2`). The user item (Andrew W. + logout)
 * is rendered separately by the organism via NavItemExtra.
 */
export const bottomNavItems: NavItemConfig[] = [
  {
    label: 'Settings',
    icon: PhGear,
    areaAccessName: 'Administration',
    subItems: [
      { label: 'Player Subscriptions', path: '/player-subscriptions' },
      { label: 'Organizations', path: '/organizations' },
      { label: 'Locations', path: '/locations' },
      { label: 'Users', path: '/users' },
      { label: 'Roles', path: '/roles' },
      { label: 'Client', path: '/client' },
      { label: 'Notifications', path: '/notification-settings', checkIsVisible: (user) => user.isAnalytics() },
      { label: 'Auditing', path: '/auditing', checkIsVisible: (user) => user.isAuditor() },
    ],
  },
  {
    label: 'Help',
    path: '/knowledge-base',
    icon: PhQuestion,
  },
];

export function isVisible(item: NavItemConfig | NavSubItemConfig, user: any): boolean {
  const areaOk =
    !item.areaAccessName || user.hasReadAccessToArea(item.areaAccessName);
  const customOk = !item.checkIsVisible || item.checkIsVisible(user);
  return areaOk && customOk;
}

export function filterItems(items: NavItemConfig[], user: any): NavItemConfig[] {
  return items
    .filter((i) => isVisible(i, user))
    .map((i) =>
      i.subItems
        ? { ...i, subItems: i.subItems.filter((s) => isVisible(s, user)) }
        : i,
    );
}
